import React, { useState, useEffect, useCallback } from 'react';
import { message, Card, Form, Input, Button, Select, DatePicker, Row, Col, Avatar, Typography, List, Tag, Empty, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../server/server';
import { getActiveCountries } from '../../server/configuration/FiltersServer';

const { Option } = Select;
const { Title } = Typography;

const EventMiniScraper = ({ profiles, profilesLoading, musicGenres, searchProfiles }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  
  // Search state
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Location state
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  
  // EventMinis state
  const [eventMinis, setEventMinis] = useState([]);
  const [eventMinisLoading, setEventMinisLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    setSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm);
      setSearchResults(results);
    } catch (error) {
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  }, [searchProfiles]);

  // Fetch EventMinis based on selected date
  const fetchEventMinisForDate = useCallback(async (date) => {
    if (!date) {
      setEventMinis([]);
      return;
    }

    setEventMinisLoading(true);
    try {
      // Create date range for the selected day
      const startDate = dayjs(date).startOf('day').toISOString();
      const endDate = dayjs(date).endOf('day').toISOString();
      
      const response = await apiClient.get('/discovery/event-minis', {
        params: {
          startDate,
          endDate,
          sort: 'upcoming',
          limit: 50
        }
      });
      
      if (response.data && response.data.data) {
        setEventMinis(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch EventMinis:', error);
      message.error('Failed to load events for selected date');
      setEventMinis([]);
    } finally {
      setEventMinisLoading(false);
    }
  }, []);

  const handleProfileSearch = (value) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    
    setSearchLoading(true);
    const timeout = setTimeout(() => {
      debouncedSearch(value.trim());
    }, 300);
    
    setSearchTimeout(timeout);
  };

  // Load countries
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getActiveCountries();
        if (response && response.data) {
          setCountries(response.data);
        }
      } catch (error) {
        message.error('Failed to load countries');
      }
    };
    loadCountries();
  }, []);

  // Update search results when initial profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0 && searchResults.length === 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleProfileSelect = (value) => {
    const selected = searchResults.find(p => p._id === value);
    setSelectedProfile(selected);
    
    // Auto-prefill start time (today at 8 PM) and end time (tomorrow at 3 AM)
    const currentStartTime = form.getFieldValue('startTime');
    const currentEndTime = form.getFieldValue('endTime');
    
    if (!currentStartTime) {
      const startTime = dayjs().hour(20).minute(0).second(0);
      form.setFieldsValue({ startTime });
      setSelectedDate(startTime);
      fetchEventMinisForDate(startTime);
    }
    
    if (!currentEndTime) {
      const endTime = dayjs().add(1, 'day').hour(3).minute(0).second(0);
      form.setFieldsValue({ endTime });
    }
    
    // Auto-select music type from the selected profile if event music type is empty
    const currentMusicType = form.getFieldValue('musicType');
    if (!currentMusicType && selected?.musicTypes?.length > 0) {
      // For EventMini, we only support single musicType, so take the first one
      form.setFieldsValue({ musicType: selected.musicTypes[0] });
    }

    // Auto-select country and city from the selected profile
    if (selected?.country) {
      // Find the country in our countries list
      const profileCountry = countries.find(c => {
        // Handle different country data structures
        if (typeof selected.country === 'object' && selected.country !== null) {
          return c._id === selected.country._id || 
                 c.name === selected.country.name ||
                 c.code === selected.country.code;
        } else {
          // If country is just an ID string
          return c._id === selected.country;
        }
      });
      
      if (profileCountry) {
        form.setFieldsValue({ country: profileCountry._id });
        setSelectedCountryId(profileCountry._id);
        setCities(profileCountry.cities || []);
        
        // Auto-select city if profile has one and it exists in the country's cities
        if (selected?.city && profileCountry.cities) {
          const profileCity = profileCountry.cities.find(city => {
            // Handle different city data structures
            if (typeof selected.city === 'object' && selected.city !== null) {
              return city._id === selected.city._id || 
                     city.name === selected.city.name;
            } else {
              // If city is just an ID string
              return city._id === selected.city;
            }
          });
          
          if (profileCity) {
            form.setFieldsValue({ city: profileCity._id });
          }
        }
      }
    }
  };

  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      setSelectedCountryId(countryId);
      setCities(selectedCountry.cities || []);
      form.setFieldsValue({ city: null });
    }
  };

  const handleStartTimeChange = (date) => {
    if (date) {
      setSelectedDate(date);
      fetchEventMinisForDate(date);
    }
  };

  const handleEndTimeChange = (date) => {
    // Optional: Could update based on end time as well if needed
  };

  const handleCreateEventMini = async (values) => {
    try {
      setLoading(true);

      const eventMiniPayload = {
        creater: values.creater,
        title: values.title,
        startTime: values.startTime?.toISOString(),
        endTime: values.endTime?.toISOString(),
        musicType: values.musicType || undefined,
        city: values.city || undefined,
        flyer: values.flyer || undefined,
        affiliatedProfiles: values.affiliatedProfiles || []
      };

      await apiClient.post('/discovery/event-minis', eventMiniPayload);
      message.success('EventMini created successfully!');
      form.resetFields();
      
      // Refresh the events list if we have a selected date
      if (selectedDate) {
        fetchEventMinisForDate(selectedDate);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      message.error(`Failed to create EventMini: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={3}>
        <PlusOutlined /> EventMini Creator
      </Title>
      
      <Card title="Create EventMini - Fast & Simple Event Creation">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEventMini}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Event Title"
                rules={[{ required: true, message: 'Please enter event title' }]}
              >
                <Input placeholder="Friday Night House Vibes at Luna Bar" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="creater"
                label="Creator Profile"
                rules={[{ required: true, message: 'Please select a creator profile!' }]}
              >
                <Select
                  placeholder="Search for a profile"
                  loading={profilesLoading || searchLoading}
                  showSearch
                  onSearch={handleProfileSearch}
                  onChange={handleProfileSelect}
                  filterOption={false}
                  options={searchResults.map(profile => ({
                    value: profile._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={profile.picture} 
                          size={24} 
                          style={{ marginRight: 8 }}
                        />
                        <span>{profile.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="musicType"
                label="Music Type (Optional)"
              >
                <Select placeholder="Select music type" allowClear>
                  {musicGenres
                    .sort((a, b) => a.value.localeCompare(b.value))
                    .map(type => (
                      <Option key={type._id} value={type._id}>{type.value}</Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <DatePicker 
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm" 
                  className="w-full" 
                  placeholder="Select start date and time"
                  onChange={handleStartTimeChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[{ required: true, message: 'Please select end time' }]}
              >
                <DatePicker 
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm" 
                  className="w-full"
                  placeholder="Select end date and time"
                  onChange={handleEndTimeChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="country"
                label="Country (Optional)"
              >
                <Select
                  placeholder="Select country"
                  onChange={handleCountryChange}
                  showSearch
                  allowClear
                  filterOption={(input, option) => {
                    const country = countries.find(c => c._id === option.value);
                    return country?.name?.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {countries.map(country => (
                    <Option key={country._id} value={country._id}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label="City (Optional)"
              >
                <Select placeholder="Select city" showSearch allowClear>
                  {cities.map(city => (
                    <Option key={city._id} value={city._id}>
                      {city.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="flyer"
                label="Flyer URL (Optional)"
                rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
              >
                <Input placeholder="https://example.com/flyer.jpg" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="affiliatedProfiles"
                label="Affiliated Profiles (Optional)"
              >
                <Select
                  mode="multiple"
                  placeholder="Search and select affiliated profiles"
                  loading={profilesLoading || searchLoading}
                  showSearch
                  onSearch={handleProfileSearch}
                  filterOption={false}
                  options={searchResults.map(profile => ({
                    value: profile._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={profile.picture} 
                          size={24} 
                          style={{ marginRight: 8 }}
                        />
                        <span>{profile.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              size="large"
              icon={<PlusOutlined />}
            >
              Create EventMini
            </Button>
            <Button 
              className="ml-2" 
              onClick={() => form.resetFields()}
              size="large"
            >
              Clear Form
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* EventMinis for Selected Date */}
      {selectedDate && (
        <Card 
          title={`Events on ${dayjs(selectedDate).format('MMMM D, YYYY')}`}
          style={{ marginTop: 24 }}
          extra={
            <Tag color="blue">
              {eventMinis.length} event{eventMinis.length !== 1 ? 's' : ''} found
            </Tag>
          }
        >
          <Spin spinning={eventMinisLoading}>
            {eventMinis.length === 0 && !eventMinisLoading ? (
              <Empty 
                description="No events found for this date"
                style={{ padding: '40px 0' }}
              />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={eventMinis}
                renderItem={(eventMini) => (
                  <List.Item
                    actions={[
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          // You can add edit functionality here if needed
                          message.info('Event details viewed');
                        }}
                      >
                        View Details
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          src={eventMini.creater?.picture} 
                          size={48}
                        >
                          {eventMini.creater?.username?.charAt(0)?.toUpperCase()}
                        </Avatar>
                      }
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600 }}>{eventMini.title}</span>
                          {eventMini.flyer && (
                            <Tag color="green" size="small">Has Flyer</Tag>
                          )}
                          {eventMini.musicType && (
                            <Tag color="purple" size="small">
                              {eventMini.musicType.value || eventMini.musicType}
                            </Tag>
                          )}
                        </div>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: 4 }}>
                            <strong>Creator:</strong> {eventMini.creater?.username || 'Unknown'}
                            {eventMini.creater?.isVerified && (
                              <Tag color="gold" size="small" style={{ marginLeft: 8 }}>
                                Verified
                              </Tag>
                            )}
                          </div>
                          <div style={{ marginBottom: 4 }}>
                            <strong>Time:</strong> {' '}
                            {dayjs(eventMini.startTime).format('h:mm A')} - {' '}
                            {dayjs(eventMini.endTime).format('h:mm A')}
                            {!dayjs(eventMini.startTime).isSame(dayjs(eventMini.endTime), 'day') && (
                              <span style={{ color: '#ff4d4f', marginLeft: 8 }}>
                                (Next day)
                              </span>
                            )}
                          </div>
                          {eventMini.city && (
                            <div style={{ marginBottom: 4 }}>
                              <strong>Location:</strong> {eventMini.city.name || eventMini.city}
                            </div>
                          )}
                          {eventMini.affiliatedProfiles && eventMini.affiliatedProfiles.length > 0 && (
                            <div>
                              <strong>Affiliated:</strong> {' '}
                              {eventMini.affiliatedProfiles.map((profile, index) => (
                                <Tag key={profile._id || index} size="small" style={{ marginRight: 4 }}>
                                  {profile.username || profile}
                                </Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Spin>
        </Card>
      )}
    </div>
  );
};

export default EventMiniScraper;